/**
 * Encrypted-at-rest thumbnail cache primitives.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/AttachmentThumbnail.swift` exactly:
 *
 *   - `ThumbnailDimensionBucket` — three sizes, string-union type.
 *   - HKDF-SHA256 cache-key derivation from the root attachment key, using
 *     info string `kaether:thumb:cache:v1` (byte-for-byte iOS match).
 *   - AES-GCM seal/open with AAD binding `(attachmentId, bucket)` so a
 *     ciphertext for one attachment cannot be substituted for another.
 *   - SHA256 integrity sentinel appended inside the plaintext so the store
 *     can detect corruption that slips past GCM (e.g. DB-level bit-flip in
 *     the stored blob before the nonce/tag, though in practice GCM already
 *     covers that; the SHA256 also catches logic errors where the wrong blob
 *     is associated with a key).
 *
 * Combined ciphertext layout: nonce(12) | ciphertext | tag(16)
 * Plaintext-inside layout:    sha256(4 bytes version prefix + thumbBytes)(32) | thumbBytes
 *
 * This is a pure crypto module. Thumbnail generation (`OffscreenCanvas`
 * down-scaling to the target bucket dimensions) is the consumer's job —
 * this module only handles the encryption layer.
 *
 * Web SubtleCrypto is used throughout; requires a secure context (HTTPS or
 * localhost). All `async` functions return `Promise<Uint8Array>`.
 *
 * @module AttachmentThumbnail
 */

// ── ThumbnailDimensionBucket ──────────────────────────────────────────────────

/**
 * Three sizes, matching iOS exactly.
 *
 *   small  — 80 px longest-edge (message list row icon)
 *   medium — 320 px longest-edge (in-chat inline preview)
 *   large  — 800 px longest-edge (tap-to-expand preview; lazy-loaded)
 */
export type ThumbnailDimensionBucket = "small" | "medium" | "large";

export const THUMBNAIL_BUCKETS: readonly ThumbnailDimensionBucket[] = [
  "small",
  "medium",
  "large",
];

// ── HKDF info string (iOS parity) ─────────────────────────────────────────────

/**
 * HKDF info string for the cache-key derivation step.
 * Must remain byte-for-byte identical to `AttachmentThumbnail.swift`.
 */
export const THUMB_CACHE_KEY_HKDF_INFO = "kaether:thumb:cache:v1" as const;

// ── Error types ───────────────────────────────────────────────────────────────

export class ThumbnailCryptoError extends Error {
  override readonly name = "ThumbnailCryptoError";
  constructor(detail: string) {
    super(`Thumbnail crypto error: ${detail}`);
  }
}

export class ThumbnailIntegrityError extends Error {
  override readonly name = "ThumbnailIntegrityError";
  constructor(detail: string) {
    super(`Thumbnail integrity error: ${detail}`);
  }
}

// ── TextEncoder singleton ─────────────────────────────────────────────────────

const enc = new TextEncoder();

// ── HKDF-SHA256 helper ────────────────────────────────────────────────────────

/**
 * HKDF-SHA256: derive `outputBytes` bytes from `inputKeyBytes` under `infoStr`.
 *
 * Salt is all-zeros (32 bytes), matching iOS CryptoKit HKDF default.
 * Uses `deriveBits` to get raw bytes back rather than `deriveKey`.
 */
async function hkdfSha256(
  inputKeyBytes: Uint8Array,
  infoStr: string,
  outputBytes: number,
): Promise<Uint8Array> {
  const baseKey = await crypto.subtle.importKey(
    "raw",
    inputKeyBytes,
    { name: "HKDF" },
    false,
    ["deriveBits"],
  );
  const salt = new Uint8Array(32); // all-zeros — matches iOS
  const info = enc.encode(infoStr);
  const bits = await crypto.subtle.deriveBits(
    { name: "HKDF", hash: "SHA-256", salt, info },
    baseKey,
    outputBytes * 8,
  );
  return new Uint8Array(bits);
}

// ── Cache-key derivation ──────────────────────────────────────────────────────

/**
 * Derive a 32-byte AES-GCM key for the thumbnail cache from the attachment's
 * root key material. Uses HKDF-SHA256 with info `kaether:thumb:cache:v1`.
 *
 * The derived bytes are the same regardless of bucket — the bucket is bound
 * via AAD at seal/open time, not at key-derivation time. This matches the iOS
 * implementation.
 *
 * @param attachmentKeyBytes  Raw 32-byte root key for the attachment.
 * @returns                   Raw 32-byte AES-GCM key bytes.
 */
export async function deriveCacheKey(
  attachmentKeyBytes: Uint8Array,
): Promise<Uint8Array> {
  if (attachmentKeyBytes.byteLength !== 32) {
    throw new ThumbnailCryptoError(
      `attachmentKeyBytes must be 32 bytes, got ${attachmentKeyBytes.byteLength}`,
    );
  }
  return hkdfSha256(attachmentKeyBytes, THUMB_CACHE_KEY_HKDF_INFO, 32);
}

// ── AAD construction ──────────────────────────────────────────────────────────

/**
 * Build the AAD bytes that bind a ciphertext to a specific (attachmentId, bucket).
 *
 * Format: UTF-8(attachmentId) | 0x00 | UTF-8(bucket)
 * The null-byte separator prevents length-extension: "att1" + "small" ≠ "att" + "1small".
 *
 * Mirrors iOS AttachmentThumbnail.aadBytes(attachmentId:bucket:).
 */
export function buildAad(
  attachmentId: string,
  bucket: ThumbnailDimensionBucket,
): Uint8Array {
  const idBytes = enc.encode(attachmentId);
  const bucketBytes = enc.encode(bucket);
  const aad = new Uint8Array(idBytes.length + 1 + bucketBytes.length);
  aad.set(idBytes, 0);
  aad[idBytes.length] = 0x00;
  aad.set(bucketBytes, idBytes.length + 1);
  return aad;
}

// ── SHA-256 integrity check ───────────────────────────────────────────────────

/**
 * Compute SHA-256 over the thumbnail bytes.
 * The digest is stored alongside the thumbnail bytes inside the plaintext
 * to allow integrity verification independent of the GCM auth tag (which
 * protects the whole ciphertext but is not separately addressable).
 */
export async function sha256(data: Uint8Array): Promise<Uint8Array> {
  const buf = await crypto.subtle.digest("SHA-256", data);
  return new Uint8Array(buf);
}

// ── Seal ──────────────────────────────────────────────────────────────────────

/**
 * Encrypt `thumbBytes` under a derived cache key and bind the result to
 * `(attachmentId, bucket)` via AES-GCM AAD.
 *
 * Plaintext-inside layout: sha256(thumbBytes)(32) | thumbBytes
 *
 * Combined ciphertext layout: nonce(12) | ciphertext+tag
 * (SubtleCrypto AES-GCM returns ciphertext|tag concatenated; we prepend the
 * nonce manually — matches CryptoKit `AES.GCM.SealedBox.combined`.)
 *
 * @param thumbBytes     Raw thumbnail pixel bytes. Caller is responsible for
 *                       down-scaling to the bucket dimensions.
 * @param attachmentId   Unique identifier for the parent attachment.
 * @param bucket         Which dimension tier this thumbnail covers.
 * @param cacheKeyBytes  32-byte key derived by `deriveCacheKey`.
 * @returns              Combined ciphertext bytes (nonce | ct+tag).
 */
export async function sealThumbnail(
  thumbBytes: Uint8Array,
  attachmentId: string,
  bucket: ThumbnailDimensionBucket,
  cacheKeyBytes: Uint8Array,
): Promise<Uint8Array> {
  if (cacheKeyBytes.byteLength !== 32) {
    throw new ThumbnailCryptoError(
      `cacheKeyBytes must be 32 bytes, got ${cacheKeyBytes.byteLength}`,
    );
  }

  // Prepend SHA-256 integrity digest to the plaintext.
  const digest = await sha256(thumbBytes);
  const plaintext = new Uint8Array(32 + thumbBytes.length);
  plaintext.set(digest, 0);
  plaintext.set(thumbBytes, 32);

  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const aad = buildAad(attachmentId, bucket);

  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    cacheKeyBytes,
    { name: "AES-GCM" },
    false,
    ["encrypt"],
  );

  // SubtleCrypto returns ciphertext|tag (no nonce).
  const ctAndTag = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: nonce, tagLength: 128, additionalData: aad },
      cryptoKey,
      plaintext,
    ),
  );

  // Combined: nonce(12) | ciphertext+tag
  const combined = new Uint8Array(12 + ctAndTag.length);
  combined.set(nonce, 0);
  combined.set(ctAndTag, 12);
  return combined;
}

// ── Open ──────────────────────────────────────────────────────────────────────

/**
 * Decrypt a sealed thumbnail blob, verifying both the GCM auth tag (via AAD
 * binding to `attachmentId` + `bucket`) and the embedded SHA-256 integrity
 * digest.
 *
 * Throws `ThumbnailCryptoError` if the GCM authentication fails (wrong key,
 * tampered ciphertext, or wrong AAD).
 * Throws `ThumbnailIntegrityError` if the inner SHA-256 digest does not match
 * the decrypted thumbnail bytes.
 *
 * @param combined       Combined ciphertext (nonce(12) | ct+tag).
 * @param attachmentId   Must match what was passed to `sealThumbnail`.
 * @param bucket         Must match what was passed to `sealThumbnail`.
 * @param cacheKeyBytes  32-byte key derived by `deriveCacheKey`.
 * @returns              Raw thumbnail pixel bytes.
 */
export async function openThumbnail(
  combined: Uint8Array,
  attachmentId: string,
  bucket: ThumbnailDimensionBucket,
  cacheKeyBytes: Uint8Array,
): Promise<Uint8Array> {
  if (combined.length < 12 + 16) {
    throw new ThumbnailCryptoError(
      `Combined ciphertext too short: ${combined.length} bytes`,
    );
  }
  if (cacheKeyBytes.byteLength !== 32) {
    throw new ThumbnailCryptoError(
      `cacheKeyBytes must be 32 bytes, got ${cacheKeyBytes.byteLength}`,
    );
  }

  const nonce = combined.subarray(0, 12);
  const ctAndTag = combined.subarray(12);
  const aad = buildAad(attachmentId, bucket);

  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    cacheKeyBytes,
    { name: "AES-GCM" },
    false,
    ["decrypt"],
  );

  let plaintext: Uint8Array;
  try {
    const buf = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: nonce, tagLength: 128, additionalData: aad },
      cryptoKey,
      ctAndTag,
    );
    plaintext = new Uint8Array(buf);
  } catch {
    throw new ThumbnailCryptoError(
      "AES-GCM authentication failed — wrong key, tampered ciphertext, or wrong AAD",
    );
  }

  // Verify the inner SHA-256 integrity digest.
  if (plaintext.length < 32) {
    throw new ThumbnailIntegrityError(
      `Decrypted plaintext too short to contain integrity digest: ${plaintext.length} bytes`,
    );
  }

  const storedDigest = plaintext.subarray(0, 32);
  const thumbBytes = plaintext.subarray(32);
  const computedDigest = await sha256(thumbBytes);

  const digestOk =
    storedDigest.length === computedDigest.length &&
    storedDigest.every((b, i) => b === computedDigest[i]);

  if (!digestOk) {
    throw new ThumbnailIntegrityError(
      "SHA-256 integrity check failed — stored digest does not match thumbnail bytes",
    );
  }

  return thumbBytes;
}

// ── Base64 utils ──────────────────────────────────────────────────────────────

export function uint8ToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  return btoa(binary);
}

export function base64ToUint8(b64: string): Uint8Array {
  const binary = atob(b64);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}
